<script setup>
import ImportApikomContentAction from "@frontJs/components/actions/projectManagement/seo/ImportApikomContentAction.vue";
import ApikomContentActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/ApikomContentActionsCell.vue";
import { ArrowUpTrayIcon, PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import ExportButton from "@js/components/buttons/ExportButton.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import CrossIconCell from "@js/components/tables/cells/CrossIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PillCell from "@js/components/tables/cells/PillCell.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { tenant, websiteVariants, ongoing, pending, done, standard, premium } =
  defineProps({
    tenant: {
      type: Object,
      required: true,
    },
    websiteVariants: {
      type: Array,
      required: true,
    },
    ongoing: {
      type: Boolean,
      required: false,
    },
    pending: {
      type: Boolean,
      required: false,
    },
    done: {
      type: Boolean,
      required: false,
    },
    standard: {
      type: Boolean,
      required: false,
    },
    premium: {
      type: Boolean,
      required: false,
    },
  });

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  selectionIds,
  updateData,
  exportData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "id",
      header: () => t("label.id"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.id,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "websiteVariant",
      header: () => t("label.websiteVariant"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.websiteVariant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "groupOrder",
      header: () => t("label.group"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.group.label,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "url",
      header: () => t("label.url"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.url,
          href: props.row.original.url,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "type",
      header: () => t("label.type"),
      cell: (props) =>
        h(PillCell, {
          pillValue: props.row.original.type.label,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "date",
      header: () => t("label.date"),
      cell: (props) =>
        props.row.original.date
          ? h(TextCellV2, {
              text: formatDate(
                props.row.original.date,
                false,
                false,
                false,
                true,
                true,
              ),
            })
          : h(CrossIconCell),
    }),
    columnHelper.accessor(() => null, {
      id: "pendedAt",
      header: () => t("label.pendedAt"),
      cell: (props) =>
        props.row.original.pendedAt !== null
          ? h(TextCellV2, {
              text: formatDate(props.row.original.pendedAt, false),
            })
          : h(CrossIconCell),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(ApikomContentActionsCell, {
          tenant: tenant,
          row: innerProps.row,
          callback: updateData,
        }),
    }),
  ],
  url: route("front.projectManagement.seo.postApikomContents", {
    tenant: tenant.id,
  }),
  exportUrl: route("front.projectManagement.seo.postExportApikomContents", {
    tenant: tenant.id,
  }),
  data: {
    ongoing: ongoing,
    pending: pending,
    done: done,
    standard: standard,
    premium: premium,
  },
  selectionIdKey: "id",
  columnPinning: {
    right: ["actions"],
  },
  columnVisibility: {
    pendedAt: pending === true,
    type: !(standard || premium),
  },
});

function importCallback() {
  toast.success(t("toast.successfullyAdded"), { position: "top-right" });
  updateData();
}
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <ExportButton
        :csv-export-callback="() => exportData('csv')"
        :xlsx-export-callback="() => exportData('xlsx')"
        :disabled="selectionIds.length === 0"
      />

      <ImportApikomContentAction
        :tenant="tenant"
        :website-variants="websiteVariants"
        :callback="importCallback"
      >
        <template #button="{ setIsOpen }">
          <ActionButton
            size="small"
            :callback="
              () => {
                setIsOpen(true);
              }
            "
            :left-icon="ArrowUpTrayIcon"
          >
            {{ t("button.import") }}
          </ActionButton>
        </template>
      </ImportApikomContentAction>

      <ActionButton
        size="small"
        :href="
          route('front.projectManagement.seo.createApikomContent', {
            tenant: tenant.id,
          })
        "
        :left-icon="PlusIcon"
      >
        {{ t("button.add") }}
      </ActionButton>
    </template>
  </DataTableLayout>
</template>
